import {
  ApplicantType,
  type ApplicationFieldPath,
} from "@versatile-credit-apply/omni-apply-client"

import {
  useApplicationStore,
  useIsTopoff,
  useProviderStore,
} from "@versatile-credit-apply/sdk"

import type {
  RouteLocation,
  RouteLocationNormalized,
  RouteLocationNormalizedLoaded,
  RouteRecordRaw,
} from "vue-router"

import {
  type FormMode,
  type FormStepId,
  getFormStepForField,
  getFormFactoryDone,
  getFormSteps,
  useDynamicFormSections,
} from "./dynamic-form-factory"

export const currentModeForRoute = (
  route: RouteLocationNormalizedLoaded
): FormMode => {
  const mode = route.params.formMode as FormMode
  return mode || "standard"
}

export const currentStepForRoute = (
  route: RouteLocationNormalizedLoaded
): FormStepId | undefined => route.params.step as FormStepId

export const findNextFormStep = (
  route: RouteLocationNormalizedLoaded,
  applicantType: ApplicantType
): FormStepId | undefined => {
  const mode = currentModeForRoute(route)
  const currentStep = currentStepForRoute(route)
  const steps = getFormSteps(mode, applicantType)
  const currentIndex = steps.findIndex((step) => step === currentStep)

  return steps.find((step, index) => {
    if (index <= currentIndex) {
      return false
    }

    const sections = useDynamicFormSections(applicantType, mode, step)
    return sections.value.length > 0
  })
}

const formStepProps =
  (applicantType: ApplicantType): RouteRecordRaw["props"] =>
  (to: RouteLocationNormalized) => {
    const application = useApplicationStore()
    const formMode = currentModeForRoute(to)

    return {
      ...to.params,
      formMode,
      applyMode: application.applyMode,
      applyStage: application.applyStage,
      applicantType,
    }
  }

const formFactoryDoneRoute = (route: RouteLocation) => {
  const mode = route.params.formMode as FormMode
  if (!mode) return undefined

  const doneRoute = getFormFactoryDone(mode)
  if (!doneRoute) return undefined

  const continueTo = route.query?.continueTo
  if (continueTo) return `${doneRoute}?continueTo=${continueTo}`

  return doneRoute
}

export const FormRoutes: RouteRecordRaw[] = [
  {
    name: "application-default-start",
    path: "",
    redirect: ({ path, query }) => {
      return {
        path: `${path}/standard`,
        query,
      }
    },
  },
  {
    name: "application-default-edit",
    path: "edit",
    redirect: ({ path, query }) => {
      const field = query.field as ApplicationFieldPath

      const applicantType = field.startsWith("joint")
        ? ApplicantType.JOINT
        : ApplicantType.PRIMARY

      const step = getFormStepForField("standard", field)

      return {
        path: path.replace("edit", `${applicantType}/${step}`),
      }
    },
  },
  {
    name: "application-standard",
    path: ":formMode?",
    children: [
      {
        name: "application-type-chooser",
        path: "",
        redirect: ({ path, query }) => {
          const topoff = useIsTopoff()
          if (topoff.value) {
            return {
              path: `${path}/prefilled/primary`,
              query,
            }
          }

          return {
            path: `${path}/primary`,
            query,
          }
        },
      },
      {
        name: `application-primary`,
        path: ":applicantType(primary)/:step?",
        beforeEnter: (to) => {
          if (to.params.step === "") {
            const step = findNextFormStep(to, ApplicantType.PRIMARY)

            if (step) {
              return {
                path: `${to.path}/${step}`,
                query: to.query,
              }
            }

            return to.fullPath.replace("/primary", "/joint")
          }
        },
        component: () => import("./ApplicationFormStep.vue"),
        props: formStepProps(ApplicantType.PRIMARY),
        meta: {
          next: (route) =>
            findNextFormStep(route, ApplicantType.PRIMARY) ??
            formFactoryDoneRoute(route) ??
            "../joint",
        },
      },
      {
        name: `application-joint`,
        path: ":applicantType(joint)/:step?",
        component: () => import("./ApplicationFormStep.vue"),
        beforeEnter: (to) => {
          if (to.params.step === "") {
            const store = useApplicationStore()
            const step = findNextFormStep(to, ApplicantType.JOINT)

            if (step && store.application?.jointEnabled) {
              return {
                path: `${to.path}/${step}`,
                query: to.query,
              }
            }

            return to.fullPath.replace("/joint", "/done")
          }
        },
        props: formStepProps(ApplicantType.JOINT),
        meta: {
          next: (route) =>
            findNextFormStep(route, ApplicantType.JOINT) ?? "../done",
        },
      },
      {
        name: `application-done`,
        path: "done",
        redirect: (to) => {
          const application = useApplicationStore()
          const store = useProviderStore()

          // We need the fully qualified provider key when looking for a response
          const provider = store.currentProviderKey!
          const preauthResponse = store.findPreauthResponse(provider)

          return (formFactoryDoneRoute(to) ??
            (application.applyMode === "full" || preauthResponse))
            ? `/apply/providers/${to.params.providerKey}/terms`
            : `/apply/providers/${to.params.providerKey}/submit`
        },
      },
    ],
  },
]
